import { useMemo } from "react";
import CustomDropdown from "../shared/dropdown/CustomDropdown";
import {
  useGetVaBundleNamesQuery,
  useGetVaBundleQuery,
} from "../../app/vaApi/vaApi";
import {
  BUNDLE_BREAKDOWNS,
} from "../../mocks/va/mockData";
import {
  bundleItemsToBreakdown,
  bundleItemsToProductTypeRows,
  legacyBreakdownToProductRows,
  normalizeBundleProducts,
} from "../../helpers/vaInvoiceUtils";

function normalizeBundleCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function codesMatch(a, b) {
  const left = normalizeBundleCode(a);
  return left === normalizeBundleCode(b) && left !== "";
}

/** Hidden from Create Order bundle dropdown (still valid elsewhere if needed). */
const CREATE_ORDER_HIDDEN_BUNDLE_CODES = new Set([
  "AUD1",
  "CAP9TEST", // CAP9-Test / CAP9-TEST / CAP9_TEST
]);

export function isHiddenCreateOrderBundle(codeOrLabel) {
  return CREATE_ORDER_HIDDEN_BUNDLE_CODES.has(
    normalizeBundleCode(codeOrLabel)
  );
}

/** Prefer Shopify productType rows, then bundleProducts categories, then legacy buckets. */
function resolveProductRows({ items, bundleProducts, productRows }) {
  if (Array.isArray(items) && items.length) {
    const typeRows = bundleItemsToProductTypeRows(items);
    if (typeRows.length) return typeRows;
  }

  if (bundleProducts) {
    const rows = normalizeBundleProducts(bundleProducts);
    if (rows.length && rows.some((r) => r.included && r.text !== "None")) {
      return rows;
    }
  }

  if (productRows?.length) {
    if (productRows.some((r) => r.included && r.text !== "None")) {
      return productRows;
    }
  }

  if (Array.isArray(items) && items.length) {
    return legacyBreakdownToProductRows(bundleItemsToBreakdown(items));
  }

  return [];
}

function rowsFromShopifyBundle(bundle) {
  if (!bundle) return [];
  return resolveProductRows({
    items: bundle.items,
    bundleProducts: bundle.bundleProducts,
    productRows: bundle.productRows,
  });
}

/** True when parse payload already has Shopify-style breakdown for this code. */
function parseHasShopifyBreakdown(bundleFromParse, bundleProductsProp, code) {
  if (!codesMatch(bundleFromParse?.code, code) && !bundleProductsProp) {
    return false;
  }

  if (bundleProductsProp && codesMatch(bundleFromParse?.code, code)) {
    const rows = normalizeBundleProducts(bundleProductsProp);
    if (rows.some((r) => r.included && r.text !== "None")) return true;
  }

  if (bundleProductsProp && !bundleFromParse) {
    const rows = normalizeBundleProducts(bundleProductsProp);
    if (rows.some((r) => r.included && r.text !== "None")) return true;
  }

  if (bundleFromParse && codesMatch(bundleFromParse.code, code)) {
    if (bundleFromParse.bundleProducts) {
      const rows = normalizeBundleProducts(bundleFromParse.bundleProducts);
      if (rows.some((r) => r.included && r.text !== "None")) return true;
    }
    if (Array.isArray(bundleFromParse.items) && bundleFromParse.items.length) {
      return true;
    }
  }

  return false;
}

export default function BundleSelector({
  value,
  onChange,
  skuCode,
  skuLabel,
  bundleFromParse,
  bundleProducts: bundleProductsProp,
}) {
  const selectedCode = String(value || "").trim();

  const {
    data: namesResponse,
    isLoading: namesLoading,
    isFetching: namesFetching,
  } = useGetVaBundleNamesQuery();
  const apiNames = namesResponse?.data ?? [];

  const parseCoversSelection = useMemo(
    () =>
      parseHasShopifyBreakdown(
        bundleFromParse,
        bundleProductsProp,
        selectedCode
      ),
    [bundleFromParse, bundleProductsProp, selectedCode]
  );

  const shouldFetchBundle =
    Boolean(selectedCode) && !parseCoversSelection;

  const {
    data: bundleResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetVaBundleQuery(selectedCode, {
    skip: !shouldFetchBundle,
  });

  const fetchedBundle = bundleResponse?.data ?? null;
  /** When parse covers selection, skip is true but RTK Query still holds the last fetch. */
  const activeFetchedBundle = shouldFetchBundle ? fetchedBundle : null;

  const bundleOptions = useMemo(() => {
    const fromApi = apiNames
      .filter(
        (b) =>
          !isHiddenCreateOrderBundle(b.code) &&
          !isHiddenCreateOrderBundle(b.label)
      )
      .map((b) => ({
        value: b.code,
        label: b.label || b.code,
      }));

    if (
      selectedCode &&
      !isHiddenCreateOrderBundle(selectedCode) &&
      !fromApi.some((o) => codesMatch(o.value, selectedCode))
    ) {
      return [
        { value: selectedCode, label: selectedCode },
        ...fromApi,
      ];
    }

    return fromApi;
  }, [apiNames, selectedCode]);

  const productRows = useMemo(() => {
    if (parseCoversSelection) {
      const parseItems =
        bundleFromParse &&
        codesMatch(bundleFromParse.code, selectedCode) &&
        Array.isArray(bundleFromParse.items)
          ? bundleFromParse.items
          : [];

      const fromParse = resolveProductRows({
        items: parseItems,
        bundleProducts:
          bundleProductsProp ||
          (codesMatch(bundleFromParse?.code, selectedCode)
            ? bundleFromParse?.bundleProducts
            : null),
      });
      if (fromParse.length) return fromParse;
    }

    if (shouldFetchBundle && (isLoading || isFetching)) {
      return legacyBreakdownToProductRows({
        glasses: "Loading…",
        accessories: "Loading…",
        handheld: "Loading…",
        lenses: "Loading…",
      });
    }

    if (shouldFetchBundle && isError) {
      return legacyBreakdownToProductRows({
        glasses: "Unable to load from Shopify",
        accessories: "None",
        handheld: "None",
        lenses: "None",
      });
    }

    const shopifyRows = rowsFromShopifyBundle(activeFetchedBundle);
    if (shopifyRows.length) {
      return shopifyRows;
    }

    return legacyBreakdownToProductRows(
      BUNDLE_BREAKDOWNS[selectedCode] || {
        glasses: "—",
        accessories: "None",
        handheld: "None",
        lenses: "None",
      }
    );
  }, [
    selectedCode,
    activeFetchedBundle,
    parseCoversSelection,
    bundleFromParse,
    bundleProductsProp,
    shouldFetchBundle,
    isLoading,
    isFetching,
    isError,
  ]);

  const showNamesLoading =
    (namesLoading || namesFetching) && !apiNames.length;
  const showBundleLoading =
    shouldFetchBundle && (isLoading || isFetching) && !activeFetchedBundle;

  return (
    <div>
      <label className="cmn_label va-review-bold-label d-block">
        Bundle <span className="text-danger">*</span>
      </label>
      <CustomDropdown
        label={
          showNamesLoading
            ? "Loading bundles…"
            : showBundleLoading
              ? "Loading bundle…"
              : "Select bundle"
        }
        value={value}
        onSelect={(opt) => onChange(opt.value)}
        options={bundleOptions}
        className="w-100"
      />
      <ul className="bundle-breakdown">
        {productRows.map((row) => (
          <li key={row.key}>
            <span>{row.label}</span>
            <span
              className={
                !row.included ||
                row.text === "None" ||
                row.text === "Loading…" ||
                row.text === "—"
                  ? "text-muted"
                  : ""
              }
            >
              {row.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
